import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Certification from "../../components/Certification/Certification";
import Footer from "../../components/Footer/Footer";
import People from "../../components/People/People";
import PartnersGrid from "../../components/PartnersGrid/PartnersGrid";
import ClientsSlider from "../../components/ClientsSlider/ClientsSlider";
import {
  FaEye,
  FaBuilding,
  FaCertificate,
  FaAward,
  FaUserCheck,
  FaClock,
  FaGlobe,
} from "react-icons/fa";
import "./About.css";

// Helper function to highlight key terms in text
const highlightText = (text, isArabic = false) => {
  // Key terms to highlight (English and Arabic)
  const highlightTerms = isArabic
    ? [
        // Arabic terms
        { pattern: /ستارغيت ش\.م\.م\.|ستارغيت|ستار غيت/gi, className: "text-highlight" },
        { pattern: /رقم\s*331/gi, className: "text-highlight" },
        { pattern: /خمسة وعشرون سنة|25 سنة/gi, className: "text-highlight" },
        { pattern: /لبنان/gi, className: "text-highlight" },
        { pattern: /التأمين/gi, className: "text-highlight" },
        { pattern: /صلاح جمعة|زهير الحاج حسن/gi, className: "text-highlight" },
      ]
    : [
        // English terms
        { pattern: /STARGATE\s*S\.A\.R\.L/gi, className: "text-highlight" },
        { pattern: /license number\s*331/gi, className: "text-highlight" },
        { pattern: /\b(thirty|25)\s*(years?|year)/gi, className: "text-highlight" },
        { pattern: /Lebanon/gi, className: "text-highlight" },
        { pattern: /Salah Joumaa/gi, className: "text-highlight" },
        { pattern: /Zuhair Al hajj Hassan/gi, className: "text-highlight" },
      ];

  // Find all matches with their positions
  const matches = [];
  highlightTerms.forEach(({ pattern, className }) => {
    const regex = new RegExp(pattern.source, pattern.flags);
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        className,
      });
    }
  });

  // Sort matches by start position
  matches.sort((a, b) => a.start - b.start);

  // Merge overlapping matches
  const mergedMatches = [];
  matches.forEach((match) => {
    if (mergedMatches.length === 0) {
      mergedMatches.push(match);
    } else {
      const last = mergedMatches[mergedMatches.length - 1];
      if (match.start <= last.end) {
        // Overlapping - merge them
        last.end = Math.max(last.end, match.end);
        last.text = text.slice(last.start, last.end);
      } else {
        mergedMatches.push(match);
      }
    }
  });

  // Build parts array
  const parts = [];
  let lastIndex = 0;
  mergedMatches.forEach((match) => {
    if (match.start > lastIndex) {
      parts.push({ text: text.slice(lastIndex, match.start), highlight: false });
    }
    parts.push({ text: match.text, highlight: true, className: match.className });
    lastIndex = match.end;
  });
  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex), highlight: false });
  }

  return parts.length > 0 ? parts : [{ text, highlight: false }];
};

const About = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("show");
        } else {
          entry.target.classList.remove("show");
        }
      });
    });

    const hiddenElements = document.querySelectorAll(".hidden");
    hiddenElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      hiddenElements.forEach((element) => observer.unobserve(element));
    };
  }, []);

  const values = [
    { key: "integrity", Icon: FaAward },
    { key: "excellence", Icon: FaCertificate },
    { key: "customerFocus", Icon: FaUserCheck },
    { key: "promptness", Icon: FaClock },
    { key: "diversity", Icon: FaGlobe },
  ];

  const teamMembers = t("about.team.members", { returnObjects: true }) || [];

  const certificates = [
    "/certifications/cert1.jpeg",
    "/certifications/cert2.jpeg",
    "/certifications/cert3.jpeg",
    "/certifications/cert4.jpeg",
    "/certifications/cert5.jpeg",
    "/certifications/cert6.jpeg",
    "/certifications/cert7.jpeg",
    "/certifications/cert8.jpeg",
    "/certifications/cert9.jpeg",
    "/certifications/cert10.jpeg",
    "/certifications/cert11.jpeg",
    "/certifications/cert12.jpeg",
    "/certifications/cert13.jpeg",
    "/certifications/cert15.jpeg",
    "/certifications/cert16.jpeg",
    "/certifications/cert17.jpeg",
    "/certifications/cert18.jpeg",
    "/certifications/cert19.jpeg",
    "/certifications/cert20.jpeg",
    "/certifications/cert21.jpeg",
    "/certifications/cert22.jpeg",
    "/certifications/cert23.jpeg",
    "/certifications/cert24.jpeg",
    "/certifications/cert25.jpeg",
    "/certifications/cert26.jpeg",
  ];

  return (
    <>
      <div className="header-margin">
        {/* Vision and Profile Section - Side by Side */}
        <section className="section section--full section--vision-profile">
          <div className="container">
            <div className="about-vision-profile-grid hidden">
              {/* Vision Card */}
              <div className="about-card about-card--vision">
                <div className="about-card-image-wrapper">
                  <div className="about-card-image-placeholder">
                    <FaEye className="placeholder-icon" />
                  </div>
                  <img
                    src="https://i.ibb.co/sQWZGz2/vision.jpg"
                    alt="vision"
                    className="about-card-image"
                    onLoad={(e) => {
                      e.target.style.opacity = "1";
                      const placeholder = e.target.previousElementSibling;
                      if (placeholder) {
                        placeholder.style.opacity = "0";
                        setTimeout(() => {
                          placeholder.style.display = "none";
                        }, 400);
                      }
                    }}
                  />
                </div>
                <div className="about-card-content-wrapper">
                  <h3 className="about-card-title">{t("about.vision.title")}</h3>
                  <div className="about-card-icon">
                    <FaEye />
                  </div>
                  <div className="about-card-content">
                    {(() => {
                      const text = t("about.vision.text");
                      // Split by periods for English, handle Arabic differently
                      const paragraphs = isArabic
                        ? text.split(/\.\s+/).filter((p) => p.trim())
                        : text.split(/\.(?=\s+[A-Z])/).filter((p) => p.trim());
                      return paragraphs.map((paragraph, idx, arr) => {
                        const fullText = paragraph.trim() + (idx < arr.length - 1 && !isArabic ? '.' : (isArabic && idx < arr.length - 1 ? '. ' : ''));
                        const parts = highlightText(fullText, isArabic);
                        return (
                          <p key={idx}>
                            {parts.map((part, partIdx) =>
                              part.highlight ? (
                                <mark key={partIdx} className={part.className}>
                                  {part.text}
                                </mark>
                              ) : (
                                <React.Fragment key={partIdx}>{part.text}</React.Fragment>
                              )
                            )}
                          </p>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>

              {/* Profile Card */}
              <div className="about-card about-card--profile">
                <div className="about-card-image-wrapper">
                  <div className="about-card-image-placeholder">
                    <FaBuilding className="placeholder-icon" />
                  </div>
                  <img
                    src="https://i.ibb.co/gMZqfHL/image-profile.jpg"
                    alt="profile"
                    className="about-card-image"
                    onLoad={(e) => {
                      e.target.style.opacity = "1";
                      const placeholder = e.target.previousElementSibling;
                      if (placeholder) {
                        placeholder.style.opacity = "0";
                        setTimeout(() => {
                          placeholder.style.display = "none";
                        }, 400);
                      }
                    }}
                  />
                </div>
                <div className="about-card-content-wrapper">
                  <h3 className="about-card-title">{t("about.profile.title")}</h3>
                  <div className="about-card-icon">
                    <FaBuilding />
                  </div>
                  <div className="about-card-content">
                    {(() => {
                      const text = t("about.profile.text");
                      // Split by periods for English, handle Arabic differently
                      const paragraphs = isArabic
                        ? text.split(/\.\s+/).filter((p) => p.trim())
                        : text.split(/\.(?=\s+[A-Z])/).filter((p) => p.trim());
                      return paragraphs.map((paragraph, idx, arr) => {
                        const fullText = paragraph.trim() + (idx < arr.length - 1 && !isArabic ? '.' : (isArabic && idx < arr.length - 1 ? '. ' : ''));
                        const parts = highlightText(fullText, isArabic);
                        return (
                          <p key={idx}>
                            {parts.map((part, partIdx) =>
                              part.highlight ? (
                                <mark key={partIdx} className={part.className}>
                                  {part.text}
                                </mark>
                              ) : (
                                <React.Fragment key={partIdx}>{part.text}</React.Fragment>
                              )
                            )}
                          </p>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="section section--values">
          <div className="container">
            <h2 className="section-title section-title--white">
              {t("about.values.title")}
            </h2>
            <div className="about-values-grid">
              {values.map(({ key, Icon }, i) => (
                <div
                  className="about-value-card hidden"
                  key={key}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="about-value-icon">
                    <Icon />
                  </div>
                  <h3>{t(`about.values.${key}.title`)}</h3>
                  <p>{t(`about.values.${key}.text`)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Portfolio Section */}
        <section className="section section--full">
          <div className="container">
            <h2 className="section-title">{t("about.portfolio.title")}</h2>
            <div className="about-certificates-grid">
              {certificates.map((img, i) => (
                <div
                  className="about-certificate-card hidden"
                  key={i}
                  style={{ transitionDelay: `${i * 50}ms` }}
                >
                  <Certification img={img} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="section section--accent">
          <div className="container">
            <h2 className="section-title">{t("about.team.title")}</h2>
            <div className="about-team-grid">
              {teamMembers.map((member, i) => (
                <div
                  className={`about-team-card ${i === 0 ? 'about-team-card--featured' : ''} hidden`}
                  key={i}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <People
                    img={member.img}
                    name={member.name}
                    role={member.role}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partners Section */}
        <section className="section section--partners">
          <div className="container">
            <h2 className="section-title">{t("partners.title")}</h2>
            <PartnersGrid />
          </div>
        </section>

        {/* Clients Section */}
        <section className="section section--full">
          <div className="container">
            <h2 className="section-title">{t("about.clients.title")}</h2>
            <ClientsSlider />
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default About;
